import { Dialog } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { HeroEntity, ItemEntity } from '@generated/gql';
import { getHeroExperiencePercent, getHeroVirtualLevel } from '@helpers/data-helper';
import { AttributesModel } from '@models/attributes.model';
import { TranslateService } from '@ngx-translate/core';
import { HeroStatItemInputInterface } from '@shared/components/hero-stat-item/hero-stat-item-input-interface';
import { getCustomHeroDataByByte32, getHeroTitle } from '@shared/constants/custom-data.constant';
import { heroItemSlotTypes } from '@shared/constants/hero-items-slot-types.constant';
import { heroItemsBody, heroItemsSkills } from '@shared/constants/hero-items-types.constant';
import { ITEM_SLOT, ITEM_SLOT_NAMES, ITEM_TYPE } from '@shared/constants/items.constant';
import { MAIN_ROUTES } from '@shared/constants/routes.constant';

import { AvatarComponent } from '../avatar/avatar.component';
import { BarStatComponent } from '../bar-stat/bar-stat.component';
import { InfoDialogComponent } from '../info-dialog/info-dialog.component';
import { ItemDescriptionDialogComponent } from '../item-description-dialog/item-description-dialog.component';
import { ItemSlotComponent } from '../item-slot/item-slot.component';
import { LifeChancesComponent } from '../life-chances/life-chances.component';

type AvatarType = 'default' | 'boss' | 'small' | 'medium' | 'micro' | 'tiny' | 'reinforcement';

@Component({
  selector: 'app-hero-card-stats',
  standalone: true,
  imports: [AvatarComponent, BarStatComponent, LifeChancesComponent, ItemSlotComponent, RouterLink],
  templateUrl: './hero-card-stats.component.html',
  styleUrl: './hero-card-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'g-flex g-flex--align-center gap-24',
  },
})
export class HeroCardStatsComponent {
  avatarType = input<AvatarType>('small');
  isShowItems = input<boolean>(false);
  isShowExpBar = input<boolean>(false);
  isShowBarName = input<boolean>(false);
  isLongCard = input<boolean>(false);
  canClickItem = input<boolean>(false);
  isShowBg = input<boolean>(false);
  avatarUrl = input<string>('assets/images/ui/guilds/account-avatar.avif');
  hero = input.required<HeroEntity>();
  isShowKarma = input<boolean>(false);

  protected readonly heroAttributes = signal<AttributesModel | null>(null);
  protected readonly heroExpPercent = signal<number>(0);
  protected readonly heroState = signal<string | null>(null);
  protected readonly virtualLevel = signal<string>('0');
  protected readonly expBarWidth = signal<number>(0);
  protected readonly heroItems = signal<ItemEntity[]>([]);
  protected readonly heroTitle = signal('Unknown');
  protected readonly karma = signal(0);
  protected readonly customHeroData = signal<HeroStatItemInputInterface[]>([]);

  protected readonly ITEM_SLOT = ITEM_SLOT;
  protected readonly heroItemSlotTypes = heroItemSlotTypes;

  private dialog = inject(Dialog);
  private translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(
      () => {
        if (this.hero()) {
          this.heroAttributes.set(AttributesModel.fromStringArray(this.hero().attributes));

          this.getHeroState();
          this.getHeroVirtualLevel();
          this.getHeroExp();
          this.getHeroItems();
          this.getHeroCustomData();
          this.getKarma();

          this.heroTitle.set(getHeroTitle(this.hero().customData));
        }
      },
      { allowSignalWrites: true },
    );
  }

  onShowStateDialog() {
    const title = `hero-info.status.${this.heroState()}.title`;
    const desc = `hero-info.status.${this.heroState()}.desc`;

    this.dialog.open(InfoDialogComponent, {
      panelClass: 'app-overlay-pane',
      data: {
        infoTitle: this.translateService.instant(title),
        infoDesc: this.translateService.instant(desc),
        customWidth: 800,
      },
    });
  }

  getHeroItem(typeId: number[]) {
    return this.heroItems().find(item => typeId.includes(item.meta.itemType));
  }

  getSlotIcon(slot: ITEM_SLOT): string {
    return `url(assets/images/ui/frames/item-icons/${ITEM_SLOT_NAMES.get(slot)?.toLowerCase().replace(' ', '-')}.avif)`;
  }

  openItemDescriptionDialog(item?: ItemEntity): void {
    if (!item) {
      return;
    }

    this.dialog.open(ItemDescriptionDialogComponent, {
      panelClass: 'app-overlay-pane-full-width',
      data: { item, hero: this.hero, isShowButtons: true },
    });
  }

  openItemInfoDialog() {
    /*const dialog = this.dialog.open(this.invComponent, {
      panelClass: 'app-overlay-pane',
      data: { heroToken: this.hero()?.meta.id, heroId: this.hero()?.heroId },
    });

    dialog.closed
      .pipe(
        filter(isNeedToUpdate => isNeedToUpdate === true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();*/
  }

  onShowHeroTitleDialog() {
    this.dialog.open(InfoDialogComponent, {
      panelClass: 'app-overlay-pane',
      data: {
        infoTitle: 'Hero Title',
        infoDesc: 'Title depends on your karma',
      },
    });
  }

  private getHeroState() {
    if (this.hero().staked) {
      this.heroState.set('reinforcement');
    } else if (this.hero().dungeon) {
      this.heroState.set('dunge');
    } else if (!this.hero().dungeon) {
      this.heroState.set('rdy');
    }
  }

  private getHeroVirtualLevel() {
    this.virtualLevel.set(getHeroVirtualLevel(this.hero().stats.experience, this.hero().stats.level).toFixed());
  }

  private getHeroExp() {
    this.heroExpPercent.set(
      Math.min(
        getHeroExperiencePercent(
          this.hero().stats.experience,
          this.hero().previousLevelExperienceRequire,
          this.hero().nextLevelExperienceRequire,
        ),
        100,
      ),
    );

    this.expBarWidth.set(Math.min(this.heroExpPercent(), 100) / 100);
  }

  private getHeroItems() {
    let skillType = 1;
    let ringType = 1;

    const heroItems = [...this.hero().items].map(item => {
      if (item.meta.itemType === ITEM_TYPE.SKILL) {
        (item.meta.itemType as unknown as number) = item.meta.itemType + skillType;
        skillType++;
      }

      if (item.meta.itemType === ITEM_TYPE.RING) {
        (item.meta.itemType as unknown as number) = item.meta.itemType * ringType;
        ringType = ringType * 10;
      }

      return item;
    });

    this.heroItems.set(heroItems);
  }

  private getHeroCustomData() {
    let customHeroData: HeroStatItemInputInterface[] = [];

    const heroData = getCustomHeroDataByByte32();

    for (const d of this.hero().customData) {
      customHeroData.push({
        id: d.dataIndex,
        name: heroData.get(d.dataIndex) ?? 'unknown',
        text: d.value,
        icon: 'unknown',
        attributeIndex: +d.dataIndex,
      });
    }

    customHeroData = customHeroData.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

    this.customHeroData.set(customHeroData);
  }

  private getKarma() {
    this.karma.set(
      Number(this.customHeroData().filter(d => d.name.toLowerCase() === 'karma')[0]?.text ?? '1000') - 1000,
    );
  }

  protected readonly heroItemsBody = heroItemsBody;
  protected readonly heroItemsSkills = heroItemsSkills;
  protected readonly MAIN_ROUTES = MAIN_ROUTES;
}
